import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type {
  WithdrawalDetailResponse,
  WithdrawalRequestDetail,
} from '../../services/merchantWithdrawalRequestsService';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import { formatVnDateTimeShort } from '../../utils/vnDateTime';
import { withAdminPathPrefix } from '../../utils/adminPathPrefix';

const { Text, Title } = Typography;

const CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

function subAccountStatusTag(status?: string | null) {
  switch (status) {
    case 'ACTIVE':
      return <Tag color="success">ACTIVE</Tag>;
    case 'SUSPENDED':
      return <Tag color="error">SUSPENDED</Tag>;
    case 'PENDING_ACTIVATION':
      return <Tag color="processing">PENDING_ACTIVATION</Tag>;
    default:
      return status ? <Tag>{status}</Tag> : <Tag color="default">Chưa có</Tag>;
  }
}

function balanceTone(
  amount: number | null | undefined,
  compareTo: number
): 'success' | 'warning' | 'danger' | undefined {
  if (amount == null || !Number.isFinite(amount)) return undefined;
  if (amount >= compareTo) return 'success';
  if (amount >= compareTo * 0.8) return 'warning';
  return 'danger';
}

interface Props {
  detail: WithdrawalDetailResponse;
  req: WithdrawalRequestDetail;
  pathname: string;
  refreshing: boolean;
  onRefresh: () => void;
}

export function WithdrawalReviewPanel({
  detail,
  req,
  pathname,
  refreshing,
  onRefresh,
}: Props) {
  const hints = detail.reviewHints;
  const sub = detail.subAccount;
  const partner = detail.partnerLiquidity;
  const totalDebit =
    hints?.totalDebitVnd ?? req.requested_amount_vnd + req.withdrawal_fee_vnd;
  const liveWithdrawable = detail.withdrawableLive?.balanceVnd ?? null;
  const appotaBalance = sub?.appotapayBalanceVnd ?? null;
  const partnerBalance = partner?.balanceVnd ?? null;
  const isPending = req.status === 'PENDING';

  return (
    <Card
      title="Kiểm duyệt & đối soát AppotaPay"
      style={CARD_STYLE}
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={onRefresh}
        >
          Làm mới số dư AppotaPay
        </Button>
      }
    >
      {isPending && hints ? (
        <Alert
          type={
            hints.canApprove
              ? 'success'
              : hints.blockers.length
              ? 'error'
              : 'warning'
          }
          showIcon
          icon={
            hints.canApprove ? (
              <CheckCircleOutlined />
            ) : hints.blockers.length ? (
              <CloseCircleOutlined />
            ) : (
              <ExclamationCircleOutlined />
            )
          }
          message={
            hints.canApprove
              ? 'Có thể duyệt chi hộ — các điều kiện cơ bản đã đạt'
              : hints.blockers.length
              ? 'Chưa thể duyệt — cần xử lý các điểm chặn bên dưới'
              : 'Cần xem xét thêm trước khi duyệt'
          }
          description={
            hints.blockers.length || hints.warnings.length ? (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {hints.blockers.map(item => (
                  <Text key={item.code} type="danger">
                    • {item.message}
                  </Text>
                ))}
                {hints.warnings.map(item => (
                  <Text key={item.code} type="warning">
                    • {item.message}
                  </Text>
                ))}
              </Space>
            ) : null
          }
          style={{ marginBottom: 20 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
            <Statistic
              title="Cần chi (rút + phí)"
              value={totalDebit}
              formatter={v => formatCurrencyVND(Number(v))}
              valueStyle={{ fontSize: 18, color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
            <Statistic
              title="Có thể rút (nội bộ)"
              value={liveWithdrawable ?? 0}
              formatter={v =>
                liveWithdrawable != null ? formatCurrencyVND(Number(v)) : '—'
              }
              valueStyle={{
                fontSize: 18,
                color:
                  balanceTone(liveWithdrawable, totalDebit) === 'danger'
                    ? '#cf1322'
                    : balanceTone(liveWithdrawable, totalDebit) === 'warning'
                    ? '#d48806'
                    : '#389e0d',
              }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Snapshot lúc gửi: {formatCurrencyVND(req.balance_snapshot_vnd)}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
            <Statistic
              title="Số dư AppotaPay merchant (REFER)"
              value={appotaBalance ?? 0}
              prefix={
                sub?.synced ? <SyncOutlined spin={refreshing} /> : undefined
              }
              formatter={v =>
                appotaBalance != null ? formatCurrencyVND(Number(v)) : '—'
              }
              valueStyle={{
                fontSize: 18,
                color:
                  balanceTone(appotaBalance, totalDebit) === 'danger'
                    ? '#cf1322'
                    : balanceTone(appotaBalance, totalDebit) === 'warning'
                    ? '#d48806'
                    : undefined,
              }}
            />
            {sub?.appotapayLastSyncedAt ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Sync: {formatVnDateTimeShort(sub.appotapayLastSyncedAt)}
                {sub.syncSource ? ` · ${sub.syncSource}` : ''}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Chưa đồng bộ từ AppotaPay
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
            <Statistic
              title="Số dư Master (tham khảo)"
              value={partnerBalance ?? 0}
              formatter={v =>
                partnerBalance != null ? formatCurrencyVND(Number(v)) : '—'
              }
              valueStyle={{
                fontSize: 18,
                color:
                  partner?.sufficient === false
                    ? '#cf1322'
                    : partner?.sufficient
                    ? '#389e0d'
                    : undefined,
              }}
            />
            {partner?.sufficient === true ? (
              <Tag color="default" style={{ marginTop: 4 }}>
                Không dùng để chi hộ
              </Tag>
            ) : partner?.sufficient === false ? (
              <Tag color="warning" style={{ marginTop: 4 }}>
                Thấp (tham khảo)
              </Tag>
            ) : partner?.error ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {partner.error}
              </Text>
            ) : null}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={12}>
          <Title level={5} style={{ marginTop: 0 }}>
            Tài khoản AppotaPay merchant (REFER)
          </Title>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Trạng thái">
              {subAccountStatusTag(sub?.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Refer ID">
              <Text copyable={sub?.referId ? { text: sub.referId } : undefined}>
                {sub?.referId || '—'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Partner code (REFER)">
              <Text code>
                {sub?.usedPartnerCode || sub?.partnerCode || '—'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Credentials">
              {sub?.hasCredentials ? (
                <Tag color="success">Đã có</Tag>
              ) : (
                <Tag>Chưa có</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Tên tài khoản">
              {sub?.accountName || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Lỗi sync gần nhất">
              {sub?.appotapaySyncError || sub?.balanceError ? (
                <Text type="danger">
                  {sub.appotapaySyncError || sub.balanceError}
                </Text>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
          <Link
            to={withAdminPathPrefix(
              pathname,
              `/appotapay/merchants/${req.restaurant_id}`
            )}
          >
            <Button type="link" style={{ paddingLeft: 0, marginTop: 8 }}>
              Mở trang Merchant AppotaPay →
            </Button>
          </Link>
        </Col>
        <Col xs={24} lg={12}>
          <Title level={5} style={{ marginTop: 0 }}>
            Checklist duyệt
          </Title>
          <List
            size="small"
            dataSource={[
              {
                key: 'bank',
                ok: Boolean(
                  req.bank_code &&
                    req.bank_account_number &&
                    req.bank_account_name
                ),
                label: 'Thông tin ngân hàng nhận đầy đủ',
              },
              {
                key: 'sub',
                ok: Boolean(sub?.hasCredentials && sub?.status === 'ACTIVE'),
                label: 'Tài khoản AppotaPay ACTIVE có credentials',
              },
              {
                key: 'referBalance',
                ok:
                  appotaBalance != null &&
                  appotaBalance >= req.requested_amount_vnd,
                label: 'Số dư AppotaPay merchant (REFER) ≥ số tiền rút',
              },
              {
                key: 'withdrawable',
                ok: liveWithdrawable != null && liveWithdrawable >= totalDebit,
                label: 'Số dư có thể rút nội bộ ≥ tiền cần chi',
              },
              {
                key: 'snapshot',
                ok:
                  liveWithdrawable == null ||
                  Math.abs(liveWithdrawable - req.balance_snapshot_vnd) <= 1000,
                label: 'Snapshot lúc gửi khớp số dư hiện tại (±1.000đ)',
              },
            ]}
            renderItem={item => (
              <List.Item>
                <Space>
                  {item.ok ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  )}
                  <Text type={item.ok ? undefined : 'danger'}>
                    {item.label}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </Card>
  );
}
